import type {
  EngineEventName,
  EngineEvents,
  EngineListener,
  FreeSpinsState,
  GameMode,
  GameSnapshot,
  GameState,
  JurisdictionProfile,
  MathConfig,
  Presenter,
  Rng,
  RoundServer,
  RoundState,
  SpinBook,
  SpinPlan,
  Wallet,
  WinTier,
} from '../types';
import { anticipationReel, forceOutcome, spin, spinWithStops, winTier } from '../math/spin';
import { Emitter } from './events';
import { DemoWallet } from './wallet';

export interface GameDeps {
  math: MathConfig;
  jurisdiction: JurisdictionProfile;
  rng: Rng;
  presenter: Presenter;
  wallet: Wallet;
  /** Platform mode: outcomes from the game server instead of the local RNG. */
  server?: RoundServer;
  /** Pause between automatic free spins (theme timing.freeSpins.betweenSpinsMs). */
  autoSpinDelayMs: number;
  restore?: RoundState;
  quickSpin?: boolean;
  /** Injectable clock (ms). */
  now?: () => number;
}

export type ForcedOutcome = { scatterCount?: number; tier?: WinTier; stops?: number[] } | null;

const round2 = (x: number) => Math.round(x * 100) / 100;
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Framework-agnostic game controller: owns the state machine, balance flow, jurisdiction rules
 * and free-spin feature. Talks to the world only through Presenter, Wallet and events.
 */
export class Game {
  private readonly emitter = new Emitter<EngineEvents>();
  private readonly math: MathConfig;
  private readonly jurisdiction: JurisdictionProfile;
  private readonly rng: Rng;
  private readonly presenter: Presenter;
  private readonly wallet: Wallet;
  private readonly server: RoundServer | null;
  private readonly autoSpinDelayMs: number;
  private readonly now: () => number;

  private state: GameState = 'loading';
  private mode: GameMode = 'base';
  private bet: number;
  private quickSpin = false;
  private freeSpins: FreeSpinsState | null = null;
  private lastBook: SpinBook | null = null;
  private balance = 0;
  private lastWin = 0;
  private lastSpinStartedAt = Number.NEGATIVE_INFINITY;
  private sessionStartedAt: number;
  private sessionWagered = 0;
  private sessionWon = 0;
  private forced: ForcedOutcome = null;
  private destroyed = false;
  private pausedFrom: GameState | null = null;
  private round: Promise<void> | null = null;

  constructor(deps: GameDeps) {
    this.math = deps.math;
    this.jurisdiction = deps.jurisdiction;
    this.rng = deps.rng;
    this.presenter = deps.presenter;
    this.wallet = deps.wallet;
    this.server = deps.server ?? null;
    this.autoSpinDelayMs = deps.autoSpinDelayMs;
    this.now = deps.now ?? (() => Date.now());
    this.sessionStartedAt = this.now();
    this.bet = this.clampBet(deps.math.defaultBet);
    this.quickSpin = !!deps.quickSpin && deps.jurisdiction.allowTurbo;
    if (deps.restore) this.applyRestore(deps.restore);
  }

  // ---------------------------------------------------------------- lifecycle

  async init(): Promise<void> {
    this.balance = await this.wallet.getBalance();
    if (this.destroyed) return;
    const grid =
      this.lastBook?.grid ??
      forceOutcome(this.math, this.rng, this.bet, 'base', (b) => b.totalWin === 0 && b.freeSpinsAwarded === 0, 100).grid;
    this.presenter.setGrid(grid);
    this.presenter.setMode(this.mode);
    this.emitter.emit('balance', { balance: this.balance });
    this.emitter.emit('bet', { bet: this.bet });
    this.emitter.emit('settings', { quickSpin: this.quickSpin });
    if (this.freeSpins) this.emitter.emit('freeSpinsUpdate', { ...this.freeSpins });
    this.setState('idle');
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.presenter.destroy();
    this.emitter.removeAll();
  }

  // ---------------------------------------------------------------- events & snapshot

  on<K extends EngineEventName>(name: K, fn: EngineListener<K>): () => void {
    return this.emitter.on(name, fn);
  }

  off<K extends EngineEventName>(name: K, fn: EngineListener<K>): void {
    this.emitter.off(name, fn);
  }

  get snapshot(): GameSnapshot {
    return {
      ...this.serialize(),
      state: this.state,
      balance: this.balance,
      lastWin: this.lastWin,
      canSpin: this.canSpin(),
      canChangeBet: this.state === 'idle' && this.mode === 'base' && !this.round,
      quickStopAvailable: this.state === 'spinning' && this.jurisdiction.allowQuickStop,
    };
  }

  serialize(): RoundState {
    const s: RoundState = {
      version: 1,
      mode: this.mode,
      bet: this.bet,
      freeSpins: this.freeSpins ? { ...this.freeSpins } : null,
      lastBook: this.lastBook,
      quickSpin: this.quickSpin,
      sessionStartedAt: this.sessionStartedAt,
      sessionWagered: this.sessionWagered,
      sessionWon: this.sessionWon,
    };
    if (this.wallet instanceof DemoWallet) s.balance = this.balance;
    return s;
  }

  private applyRestore(r: RoundState): void {
    this.mode = r.mode;
    this.bet = this.clampBet(r.bet);
    this.freeSpins = r.freeSpins && r.freeSpins.remaining > 0 ? { ...r.freeSpins } : null;
    if (!this.freeSpins) this.mode = 'base';
    this.lastBook = r.lastBook;
    this.quickSpin = r.quickSpin && this.jurisdiction.allowTurbo;
    this.sessionStartedAt = r.sessionStartedAt || this.now();
    this.sessionWagered = r.sessionWagered || 0;
    this.sessionWon = r.sessionWon || 0;
    if (typeof r.balance === 'number' && this.wallet instanceof DemoWallet) this.wallet.setBalance(r.balance);
  }

  // ---------------------------------------------------------------- settings

  getAvailableBets(): number[] {
    const max = this.jurisdiction.maxStake;
    const bets = this.math.bets.filter((b) => max == null || b <= max + 1e-9);
    return bets.length ? bets : [this.math.bets[0]];
  }

  private clampBet(total: number): number {
    const bets = this.getAvailableBets();
    if (bets.includes(total)) return total;
    // nearest allowed bet not above `total`, else the smallest
    let best = bets[0];
    for (const b of bets) if (b <= total + 1e-9) best = b;
    return best;
  }

  setBet(total: number): void {
    if (!this.snapshot.canChangeBet) return;
    const next = this.clampBet(total);
    if (next === this.bet) return;
    this.bet = next;
    this.emitter.emit('bet', { bet: this.bet });
    this.emitSnapshot();
  }

  setQuickSpin(on: boolean): void {
    const next = on && this.jurisdiction.allowTurbo;
    if (next === this.quickSpin) return;
    this.quickSpin = next;
    this.emitter.emit('settings', { quickSpin: this.quickSpin });
    this.emitSnapshot();
  }

  setBalance(balance: number): void {
    if (this.wallet instanceof DemoWallet) this.wallet.setBalance(balance);
    this.balance = round2(Math.max(0, balance));
    this.emitter.emit('balance', { balance: this.balance });
    this.emitSnapshot();
  }

  forceNext(force: ForcedOutcome): void {
    this.forced = force;
  }

  // ---------------------------------------------------------------- input

  /** Spin button / spacebar semantics. */
  press(): void {
    switch (this.state) {
      case 'idle':
        void this.spin();
        break;
      case 'spinning':
        if (this.jurisdiction.allowQuickStop) this.presenter.quickStop();
        break;
      case 'presenting':
      case 'freeSpinsIntro':
      case 'freeSpinsOutro':
        this.presenter.skip();
        break;
      default:
        break;
    }
  }

  private canSpin(): boolean {
    if (this.destroyed || this.round || this.state !== 'idle') return false;
    if (this.now() - this.lastSpinStartedAt < this.jurisdiction.minSpinMs) return false;
    if (this.mode === 'base' && this.balance + 1e-9 < this.bet) return false;
    return true;
  }

  /** Resolves when the whole round (including a full free-spin feature) has finished. */
  spin(): Promise<void> {
    if (this.round) return this.round;
    if (!this.canSpin()) {
      if (this.state === 'idle' && this.mode === 'base' && this.balance + 1e-9 < this.bet) {
        this.emitter.emit('error', { code: 'INSUFFICIENT_FUNDS', message: 'Not enough balance' });
      }
      return Promise.resolve();
    }
    return this.startRound(this.mode === 'base' ? this.bet : 0);
  }

  async buyFeature(): Promise<void> {
    if (!this.math.scatter || !this.math.bonusBuy.enabled || !this.jurisdiction.allowBonusBuy) throw new Error('BONUS_BUY_DISABLED');
    if (this.round || this.state !== 'idle' || this.mode !== 'base') throw new Error('NOT_IDLE');
    const cost = round2(this.bet * this.math.bonusBuy.costMultiplier);
    if (this.balance + 1e-9 < cost) {
      this.emitter.emit('error', { code: 'INSUFFICIENT_FUNDS', message: 'Not enough balance' });
      throw new Error('INSUFFICIENT_FUNDS');
    }
    this.forced = { scatterCount: this.math.bonusBuy.scatterCount };
    return this.startRound(cost);
  }

  pause(): void {
    if (this.destroyed || this.state === 'paused') return;
    this.pausedFrom = this.state;
    this.presenter.setPaused(true);
    this.setState('paused');
  }

  resume(): void {
    if (this.destroyed || this.state !== 'paused') return;
    const back = this.pausedFrom ?? 'idle';
    this.pausedFrom = null;
    this.presenter.setPaused(false);
    this.setState(back);
  }

  // ---------------------------------------------------------------- round flow

  private startRound(debit: number): Promise<void> {
    this.round = this.runRound(debit).finally(() => {
      this.round = null;
      if (!this.destroyed && this.state !== 'paused') this.emitSnapshot();
    });
    return this.round;
  }

  private async runRound(debit: number): Promise<void> {
    if (this.mode === 'base') {
      if (debit > 0 && this.server) {
        // Platform mode: the server debits inside /spin; the balance shown during the spin is optimistic.
        this.sessionWagered = round2(this.sessionWagered + debit);
      } else if (debit > 0) {
        try {
          this.balance = await this.wallet.debit(debit);
        } catch {
          this.forced = null;
          this.emitter.emit('error', { code: 'INSUFFICIENT_FUNDS', message: 'Not enough balance' });
          return;
        }
        if (this.destroyed) return;
        this.sessionWagered = round2(this.sessionWagered + debit);
        this.emitter.emit('balance', { balance: this.balance });
      }
      const book = await this.playSpin('base');
      if (this.destroyed || !book) return;
      if (book.freeSpinsAwarded > 0) await this.runFeature(book.freeSpinsAwarded);
    } else {
      // Restored mid-feature: continue the loop.
      await this.runFeatureLoop();
    }
    if (!this.destroyed) this.setState('idle');
  }

  private produceBook(mode: GameMode, bet: number): SpinBook {
    const f = this.forced;
    this.forced = null;
    if (f?.stops) return spinWithStops(this.math, f.stops, bet, mode);
    if (f?.scatterCount != null && this.math.scatter) {
      const n = f.scatterCount;
      const min = this.math.scatter.minCount;
      return forceOutcome(this.math, this.rng, bet, mode, (b) => b.scatterWin?.count === n || (n < min && countScatters(b, this.math) === n), 2_000_000);
    }
    if (f?.tier) {
      const tries = f.tier === 'epic' ? 4_000_000 : f.tier === 'mega' ? 1_500_000 : 400_000;
      return forceOutcome(this.math, this.rng, bet, mode, (b) => winTier(this.math, b) === f.tier, tries);
    }
    return spin(this.math, this.rng, bet, mode);
  }

  /** One spin: produce the book, animate reels, present wins. Returns null if destroyed meanwhile. */
  private async playSpin(mode: GameMode): Promise<SpinBook | null> {
    const bet = mode === 'free' && this.freeSpins ? this.freeSpins.bet : this.bet;
    this.lastSpinStartedAt = this.now();
    this.presenter.clearWin();
    let book: SpinBook;
    let settledBalance: number | null = null;
    if (this.server) {
      try {
        const res = await this.server.spin({ bet, mode, clientRoundId: newRoundId() });
        book = res.book;
        settledBalance = res.balance;
      } catch (err) {
        this.forced = null;
        const e = err as { code?: string; message?: string };
        this.emitter.emit('error', { code: e.code ?? 'NETWORK', message: e.message ?? 'Network error' });
        return null;
      }
      if (this.destroyed) return null;
      if (mode === 'base' && bet > 0) {
        this.balance = round2(Math.max(0, this.balance - bet));
        this.emitter.emit('balance', { balance: this.balance });
      }
      if (book.totalWin > 0) this.sessionWon = round2(this.sessionWon + book.totalWin);
    } else {
      book = this.produceBook(mode, bet);
    }
    this.lastBook = book;
    if (book.totalWin > 0 && !this.server) {
      this.balance = await this.wallet.credit(book.totalWin);
      if (this.destroyed) return null;
      this.sessionWon = round2(this.sessionWon + book.totalWin);
    }
    this.setState('spinning');
    this.emitter.emit('spinStart', { mode, bet });
    const plan: SpinPlan = {
      book,
      quick: this.quickSpin,
      anticipationFromReel: anticipationReel(this.math, book.grid),
      minDurationMs: this.jurisdiction.minSpinMs,
      onReelStop: (reel) => {
        if (!this.destroyed) this.emitter.emit('reelStop', { reel, column: book.grid[reel] });
      },
    };
    await this.presenter.spin(plan);
    if (this.destroyed) return null;
    this.emitter.emit('spinEnd', { book });
    this.lastWin = book.totalWin;
    this.setState('presenting');
    const tier = winTier(this.math, book);
    await this.presenter.presentWin(book, {
      tier,
      bet,
      mode,
      celebrate: book.totalWin > bet || this.jurisdiction.celebrateBelowStake,
      skippable: true,
    });
    if (this.destroyed) return null;
    if (settledBalance !== null) this.balance = round2(Math.max(0, settledBalance));
    this.emitter.emit('balance', { balance: this.balance });
    this.emitter.emit('win', { book, tier });
    this.emitSnapshot();
    return book;
  }

  private async runFeature(awarded: number): Promise<void> {
    this.freeSpins = { remaining: awarded, total: awarded, played: 0, totalWin: 0, bet: this.bet, lastRetrigger: 0 };
    this.emitter.emit('freeSpinsStart', { spins: awarded });
    this.setState('freeSpinsIntro');
    await this.presenter.freeSpinsIntro(awarded);
    if (this.destroyed) return;
    this.mode = 'free';
    this.presenter.setMode('free');
    this.emitter.emit('freeSpinsUpdate', { ...this.freeSpins });
    await this.runFeatureLoop();
  }

  private async runFeatureLoop(): Promise<void> {
    while (this.freeSpins && this.freeSpins.remaining > 0 && !this.destroyed) {
      await this.pausableDelay(this.autoSpinDelayMs);
      if (this.destroyed || !this.freeSpins) return;
      this.freeSpins.remaining--;
      this.freeSpins.played++;
      this.freeSpins.lastRetrigger = 0;
      const book = await this.playSpin('free');
      if (!book && !this.destroyed && this.freeSpins) {
        // server error mid-feature: give the spin back, the player retries with the button
        this.freeSpins.remaining++;
        this.freeSpins.played--;
        this.emitter.emit('freeSpinsUpdate', { ...this.freeSpins });
        return;
      }
      if (this.destroyed || !book || !this.freeSpins) return;
      this.freeSpins.totalWin = round2(this.freeSpins.totalWin + book.totalWin);
      if (book.freeSpinsAwarded > 0) {
        this.freeSpins.remaining += book.freeSpinsAwarded;
        this.freeSpins.total += book.freeSpinsAwarded;
        this.freeSpins.lastRetrigger = book.freeSpinsAwarded;
        this.emitter.emit('freeSpinsUpdate', { ...this.freeSpins });
        await this.presenter.freeSpinsRetrigger(book.freeSpinsAwarded, this.freeSpins.remaining);
        if (this.destroyed) return;
      }
      this.emitter.emit('freeSpinsUpdate', { ...this.freeSpins });
    }
    if (this.destroyed || !this.freeSpins) return;
    const { totalWin, played } = this.freeSpins;
    this.setState('freeSpinsOutro');
    await this.presenter.freeSpinsOutro(totalWin);
    if (this.destroyed) return;
    this.freeSpins = null;
    this.mode = 'base';
    this.presenter.setMode('base');
    this.emitter.emit('freeSpinsEnd', { totalWin, spins: played });
  }

  private async pausableDelay(ms: number): Promise<void> {
    if (ms > 0) await sleep(ms);
    while (!this.destroyed && this.state === 'paused') await sleep(100);
  }

  // ---------------------------------------------------------------- helpers

  private setState(next: GameState): void {
    if (this.destroyed) return;
    const previous = this.state;
    this.state = next;
    this.emitter.emit('state', { state: next, previous });
    this.emitSnapshot();
  }

  private emitSnapshot(): void {
    if (!this.destroyed) this.emitter.emit('snapshot', this.snapshot);
  }
}

function newRoundId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function countScatters(book: SpinBook, math: MathConfig): number {
  if (!math.scatter) return 0;
  let n = 0;
  for (const col of book.grid) for (const s of col) if (s === math.scatter.symbol) n++;
  return n;
}
